/**
 * SEO Utilities for generating metadata for different content types
 */

import {
  generateBlogOGImage,
  generateCourseOGImage,
  generateEmbedUrl,
  generateProfileOGImage,
  generateTopicOGImage,
  generateWebinarOGImage,
} from './og-image-generator'

import { MemePost } from '@/types/meme.interface'

export interface SEOData {
  title: string
  description: string
  keywords: string
  ogImage?: string
  ogType: 'website' | 'article' | 'video.other' | 'profile'
  author?: string
  publishedTime?: string
  modifiedTime?: string
  section?: string
  tags: string[]
  videoUrl?: string
  videoWidth?: number
  videoHeight?: number
  embedUrl?: string
}

// Helper function to strip HTML tags from content
export const stripHtml = (html: string): string => {
  return html
    .replace(/<[^>]*>/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

// Helper function to truncate text for meta descriptions
export const truncateText = (text: string, maxLength = 160): string => {
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength - 3) + '...'
}

// Helper function to generate hashtags for social media
export const generateHashtags = (tags: string[]): string => {
  return tags.map(tag => `#${tag.replace(/\s+/g, '')}`).join(' ')
}

// Helper function to format content for social media sharing
export const formatContentForSharing = (content: string, maxLength = 100): string => {
  const stripped = stripHtml(content)
  return truncateText(stripped, maxLength)
}

/**
 * Generate SEO data for blog posts
 */
export const generateBlogSEO = (blog: Record<string, unknown>): SEOData => {
  const authorObj = blog.author as { firstName?: string; lastName?: string } | undefined
  const author = authorObj ? `${authorObj.firstName || ''} ${authorObj.lastName || ''}`.trim() : 'FSDS Team'

  // Generate dynamic OG image
  const ogImageData = generateBlogOGImage({
    title: blog.title as string,
    metaDescription: blog.metaDescription as string,
    content: blog.content as string,
    author: authorObj,
    thumbnailImage: blog.thumbnailImage as string,
  })

  // Generate embed URL
  const embedUrl = generateEmbedUrl('blogs', blog.slug as string)

  return {
    title: blog.title as string,
    description: truncateText(stripHtml((blog.metaDescription as string) || (blog.content as string) || '')),
    keywords: `${blog.title as string}, ${blog.metaDescription as string}, data science, machine learning, FSDS, blog`,
    ogImage: ogImageData.url,
    ogType: 'article',
    author,
    publishedTime: blog.createdAt as string | undefined,
    modifiedTime: blog.updatedAt as string | undefined,
    section: 'Blog',
    tags: (blog.tags as string[]) || ['blog', 'data science', 'machine learning'],
    embedUrl,
  }
}

/**
 * Generate SEO data for meme posts
 */
export const generateMemeSEO = (meme: MemePost): SEOData => {
  const userObj = meme.user as { firstName?: string; lastName?: string } | undefined
  const author = meme.isAnonymous
    ? 'Ẩn danh'
    : userObj
      ? `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim()
      : 'FSDS Community'

  const plainContent = stripHtml(meme.content || '').trim()
  const titleFromContent = plainContent ? truncateText(plainContent, 72) : `Bài viết của ${author}`

  return {
    title: titleFromContent,
    description: plainContent ? truncateText(plainContent, 160) : 'Bài viết từ cộng đồng FSDS về data science và AI.',
    keywords: 'meme, data science, AI, machine learning, fun, community, FSDS',
    ogImage: meme.images?.[0]?.imageUrl || '',
    ogType: 'article',
    author,
    publishedTime: meme.createdAt?.toString(),
    modifiedTime: meme.updatedAt?.toString(),
    section: 'Community',
    tags: ['meme', 'community', 'data science', 'fun', 'AI'],
  }
}

/**
 * Generate SEO data for webinars
 */
export const generateWebinarSEO = (webinar: Record<string, unknown>): SEOData => {
  const authorObj = webinar.author as { firstName?: string; lastName?: string } | undefined
  const author = authorObj ? `${authorObj.firstName || ''} ${authorObj.lastName || ''}`.trim() : 'FSDS Team'

  // Generate dynamic OG image
  const ogImageData = generateWebinarOGImage({
    title: webinar.title as string,
    description: webinar.description as string,
    author: authorObj,
    thumbnailImage: webinar.thumbnailImage as string,
  })

  // Generate embed URL
  const embedUrl = generateEmbedUrl('webinars', webinar.id as string)

  return {
    title: webinar.title as string,
    description: truncateText(stripHtml((webinar.description as string) || `Webinar về ${webinar.title as string}`)),
    keywords: `${webinar.title as string}, webinar, data science, machine learning, AI, education, FSDS`,
    ogImage: ogImageData.url,
    ogType: 'video.other',
    author,
    publishedTime: webinar.createdAt as string | undefined,
    modifiedTime: webinar.updatedAt as string | undefined,
    section: 'Webinars',
    tags: ['webinar', 'data science', 'machine learning', 'AI', 'education'],
    videoUrl: webinar.videoUrl as string | undefined,
    videoWidth: 1280,
    videoHeight: 720,
    embedUrl,
  }
}

/**
 * Generate SEO data for topics/discussions
 */
export const generateTopicSEO = (topic: Record<string, unknown>, isForum = false): SEOData => {
  const userObj = topic.user as { firstName?: string; lastName?: string } | undefined
  const author = userObj ? `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() : 'FSDS Community'

  // Generate dynamic OG image
  const ogImageData = generateTopicOGImage({
    title: topic.title as string,
    content: topic.content as string,
    user: userObj || { firstName: '', lastName: '' },
    isForum,
  })

  // Generate embed URL
  const embedUrl = generateEmbedUrl(isForum ? 'forum' : 'topic', topic.id as string)

  return {
    title: topic.title as string,
    description: truncateText(stripHtml((topic.content as string) || `Thảo luận về ${topic.title as string}`)),
    keywords: `${topic.title as string}, ${isForum ? 'forum' : 'topic'}, data science, machine learning, AI, discussion, FSDS`,
    ogImage: ogImageData.url,
    ogType: 'article',
    author,
    publishedTime: topic.createdAt as string | undefined,
    modifiedTime: topic.updatedAt as string | undefined,
    section: isForum ? 'Forum' : 'Topics',
    tags: [isForum ? 'forum' : 'topic', 'data science', 'machine learning', 'AI', 'discussion'],
    embedUrl,
  }
}

/**
 * Generate SEO data for course pages
 */
export const generateCourseSEO = (course: Record<string, unknown>): SEOData => {
  // Generate dynamic OG image
  const ogImageData = generateCourseOGImage({
    title: course.title as string,
    description: course.description as string,
    thumbnailImage: course.thumbnailImage as string,
  })

  // Generate embed URL
  const embedUrl = generateEmbedUrl('courses', course.id as string)

  return {
    title: course.title as string,
    description: truncateText(
      stripHtml(
        (course.description as string) ||
          (course.content as string) ||
          `Khóa học ${course.title as string} về data science`
      )
    ),
    keywords: `${course.title as string}, course, data science, machine learning, AI, education, FSDS`,
    ogImage: ogImageData.url,
    ogType: 'article',
    author: 'FSDS Team',
    publishedTime: course.createdAt as string | undefined,
    modifiedTime: course.updatedAt as string | undefined,
    section: 'Courses',
    tags: ['course', 'data science', 'machine learning', 'AI', 'education'],
    embedUrl,
  }
}

/**
 * Generate SEO data for user profiles
 */
export const generateProfileSEO = (user: Record<string, unknown>): SEOData => {
  const fullName = `${user.firstName || ''} ${user.lastName || ''}`.trim()

  // Generate dynamic OG image
  const ogImageData = generateProfileOGImage({
    firstName: user.firstName as string,
    lastName: user.lastName as string,
    avatar: user.avatar as string,
    bio: user.bio as string,
  })

  // Generate embed URL
  const embedUrl = generateEmbedUrl('user', user.profileId as string)

  return {
    title: `${fullName}'s Profile`,
    description: truncateText(stripHtml((user.bio as string) || `Hồ sơ của ${fullName} trên FSDS`)),
    keywords: `${fullName}, profile, data science, machine learning, AI, FSDS, community`,
    ogImage: ogImageData.url,
    ogType: 'profile',
    author: fullName,
    section: 'Profiles',
    tags: ['profile', 'data science', 'community', 'FSDS'],
    embedUrl,
  }
}

/**
 * Generate SEO data for listing pages
 */
export const generateListingSEO = (
  title: string,
  description: string,
  section: string,
  tags: string[] = []
): SEOData => {
  return {
    title,
    description: truncateText(stripHtml(description)),
    keywords: `${title}, ${section}, data science, machine learning, AI, FSDS`,
    ogType: 'website',
    section,
    tags: [...tags, section.toLowerCase(), 'data science', 'FSDS'],
  }
}

/**
 * Appends title and other metadata to a URL for better social sharing and tracking
 *
 * This function enhances URLs with metadata that can be used for:
 * - Social media previews and tracking
 * - Analytics and attribution
 * - SEO optimization
 *
 * @param url - The base URL to enhance
 * @param metadata - Object containing metadata to append
 * @param metadata.title - The title of the content
 * @param metadata.description - Description of the content
 * @param metadata.hashtags - Array of hashtags
 * @param metadata.source - UTM source parameter
 * @param metadata.medium - UTM medium parameter
 * @returns Enhanced URL with metadata as query parameters
 *
 * @example
 * ```ts
 * const enhancedUrl = appendMetadataToUrl('https://example.com/article', {
 *   title: 'My Article',
 *   description: 'A great article about data science',
 *   hashtags: ['datascience', 'ai'],
 *   source: 'social_share',
 *   medium: 'facebook'
 * })
 * // Returns: https://example.com/article?title=My%20Article&description=A%20great%20article&hashtags=datascience,ai&utm_source=social_share&utm_medium=facebook
 * ```
 */
export function appendMetadataToUrl(
  url: string,
  metadata: {
    title?: string
    description?: string
    hashtags?: string[]
    source?: string
    medium?: string
  }
): string {
  const urlObj = new URL(
    url,
    typeof window !== 'undefined' ? window.location.origin : 'https://fullstackdatascience.com'
  )

  if (metadata.title) {
    urlObj.searchParams.set('title', metadata.title)
  }

  if (metadata.description) {
    urlObj.searchParams.set('description', metadata.description)
  }

  if (metadata.hashtags && metadata.hashtags.length > 0) {
    urlObj.searchParams.set('hashtags', metadata.hashtags.join(','))
  }

  if (metadata.source) {
    urlObj.searchParams.set('utm_source', metadata.source)
  }

  if (metadata.medium) {
    urlObj.searchParams.set('utm_medium', metadata.medium)
  }

  return urlObj.toString()
}

/**
 * Generates a social share URL with enhanced metadata for specific platforms
 *
 * This function creates platform-specific sharing URLs with:
 * - Enhanced URLs containing metadata
 * - Properly formatted share text with title prominently displayed
 * - Platform-specific parameters (quote, summary, hashtags, etc.)
 *
 * @param baseUrl - The base URL to be shared
 * @param platform - The social media platform
 * @param metadata - Object containing content metadata
 * @param metadata.title - The title of the content (prominently displayed)
 * @param metadata.description - Description of the content
 * @param metadata.hashtags - Array of hashtags for the content
 * @param metadata.embedUrl - Optional embed URL for embeddable content
 * @returns Platform-specific sharing URL
 *
 * @example
 * ```ts
 * const facebookUrl = generateSocialShareUrl('https://example.com/article', 'facebook', {
 *   title: 'Amazing Article',
 *   description: 'Learn about data science',
 *   hashtags: ['datascience', 'ai'],
 *   embedUrl: 'https://example.com/embed/article'
 * })
 * ```
 */
export function generateSocialShareUrl(
  baseUrl: string,
  platform: 'facebook' | 'linkedin' | 'twitter',
  metadata: {
    title: string
    description: string
    hashtags?: string[]
    embedUrl?: string
  }
): string {
  const enhancedUrl = appendMetadataToUrl(baseUrl, {
    title: metadata.title,
    description: metadata.description,
    hashtags: metadata.hashtags,
    source: 'social_share',
    medium: platform,
  })

  const shareText = metadata.embedUrl
    ? `${metadata.title}\n\n${metadata.description}\n\n${generateHashtags(metadata.hashtags || [])}\n\nEmbed: ${metadata.embedUrl}`
    : `${metadata.title}\n\n${metadata.description}\n\n${generateHashtags(metadata.hashtags || [])}`

  switch (platform) {
    case 'facebook':
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(enhancedUrl)}&quote=${encodeURIComponent(shareText)}`
    case 'linkedin':
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(enhancedUrl)}&title=${encodeURIComponent(metadata.title)}&summary=${encodeURIComponent(shareText)}`
    case 'twitter': {
      const formattedDescription = formatContentForSharing(metadata.description, 100)
      const twitterShareText = metadata.embedUrl
        ? `${metadata.title}\n\n${formattedDescription}\n\n${generateHashtags(metadata.hashtags || [])}\n\nEmbed: ${metadata.embedUrl}`
        : `${metadata.title}\n\n${formattedDescription}\n\n${generateHashtags(metadata.hashtags || [])}`
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterShareText)}&url=${encodeURIComponent(enhancedUrl)}`
    }
    default:
      return enhancedUrl
  }
}
